import { useCallback, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import {
  INFORMATION_SAVING_CODE,
  INFORMATION_SAVING_TEXT,
  INVOICE_IMPORT_STARTED,
  INVOICE_LIST_STALE,
} from 'components/invoiceImport/invoiceImportText';
import useInvoiceImportRun from 'components/invoiceImport/useInvoiceImportRun';
import UseContext from 'hooks/useContext';
import ApiDatabase from 'server';

// The page's toast follows the same run on the default key.
const RUN_ABORT_KEY = 'invoiceDetail|run';

// What each launch says when it is refused: its route, and its texts.
const LAUNCHES = {
  import: {
    call: (payload, resolve, reject) =>
      ApiDatabase.postInvoiceImport(payload, resolve, reject),
    nothingCode: 'NOTHING_TO_IMPORT',
    nothing: ['messages.nothingToImport', 'Cette facture est déjà importée.'],
    failed: [
      'messages.importFailed',
      'L’import n’a pas pu être lancé, réessayez.',
    ],
  },
  retry: {
    call: (payload, resolve, reject) =>
      ApiDatabase.postInvoiceImportRetry(payload, resolve, reject),
    nothingCode: 'NOTHING_TO_RETRY',
    nothing: ['messages.nothingToRetry', 'Cette facture n’est plus en erreur.'],
    failed: [
      'messages.retryFailed',
      'Le réessai n’a pas pu être lancé, réessayez.',
    ],
  },
};

/**
 * "Importer dans Pennylane" and "Réessayer l'import" of the detail panel
 * (JBWEB-1274): this invoice alone, on the routes of the month
 * (`invoiceNumbers: [n]`). The run goes to the page's toast, as the import
 * modal does; the panel follows it too, to read the invoice again at its end
 * (`onFinished`). A run of the month already going is followed instead.
 */
const useInvoiceDetailImport = (invoice, onFinished) => {
  const { language, setModalDataReturn } = UseContext();
  const text = (key, fallback) =>
    language.getValue(`invoiceDetail.${key}`, fallback);
  const importText = (key, fallback) =>
    language.getValue(`invoiceImport.${key}`, fallback);
  const [runId, setRunId] = useState(null);
  const [isSending, setIsSending] = useState(false);
  // Two clicks in one render would both see `isSending` false.
  const isPending = useRef(false);

  const finish = useCallback(
    (run) => {
      setRunId(null);
      onFinished?.(run);
    },
    [onFinished],
  );
  useInvoiceImportRun(runId, finish, { abortKey: RUN_ABORT_KEY });

  const follow = (id) => {
    if (!id) return;
    setModalDataReturn({ type: INVOICE_IMPORT_STARTED, runId: id });
    setRunId(id);
  };

  const launch = (kind) => {
    if (isPending.current || runId || !invoice) return;
    const { call, nothingCode, nothing, failed } = LAUNCHES[kind];
    isPending.current = true;
    setIsSending(true);
    const done = () => {
      isPending.current = false;
      setIsSending(false);
    };
    call(
      { period: invoice.period, invoiceNumbers: [invoice.number] },
      (data) => {
        done();
        follow(data?.runId);
      },
      (error) => {
        done();
        if (error?.name === 'CanceledError') return;
        // Never the error object in the console: axios keeps the request, token included.
        const body = error?.response?.data;
        if (body?.errorCode === 'IMPORT_ALREADY_RUNNING' && body.runId) {
          toast.info(
            importText(
              'alreadyRunning',
              'Un import de ce mois est déjà en cours : sa progression s’affiche.',
            ),
          );
          follow(body.runId);
          return;
        }
        if (body?.errorCode === INFORMATION_SAVING_CODE) {
          toast.info(
            importText(
              INFORMATION_SAVING_TEXT.key,
              INFORMATION_SAVING_TEXT.fallback,
            ),
          );
          return;
        }
        if (body?.errorCode === nothingCode) {
          toast.info(text(...nothing));
          // The list of the page still shows the state this answer contradicts.
          setModalDataReturn({ type: INVOICE_LIST_STALE });
          onFinished?.(null);
          return;
        }
        toast.error(text(...failed));
      },
    );
  };

  return {
    importInvoice: () => launch('import'),
    retryInvoice: () => launch('retry'),
    isBusy: isSending || Boolean(runId),
    isRunning: Boolean(runId),
  };
};

export default useInvoiceDetailImport;
